import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Modal, OverlayTrigger, Tooltip, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FaFilePdf, FaUpload, FaTimes, FaSearch, FaPlus, FaTimesCircle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { 
  useSearchResourceTagsQuery,
  useCreateResourceTagMutation,
  useGetResourceTagsQuery 
} from '../Services/SecondaryApi';
import '../Styles/RoleManagement.css';
import './AddAtozResourceModal.css';

const schema = yup.object().shape({
  name: yup.string().required('Resource name is required'),
  tooltip: yup.string().nullable(),
  pdf_file: yup.mixed()
    .test('fileSize', 'File size is too large (max 10MB)', (value) => {
      if (!value) return true; // No file is allowed (optional field)
      return value.size <= 10 * 1024 * 1024; // 10MB
    })
    .test('fileType', 'Only PDF files are allowed', (value) => {
      if (!value) return true; // No file is allowed (optional field)
      return value.type === 'application/pdf';
    }),
  video_url: yup.string()
    .url('Please enter a valid URL')
    .nullable(),
  editable_link: yup.string()
    .url('Please enter a valid URL')
    .nullable()
});

const AddAtozResourceModal = ({ show, onHide, onSaveResource, resource }) => {
  const { 
    register, 
    handleSubmit, 
    formState: { errors }, 
    reset, 
    setValue, 
    trigger,
    watch
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      tooltip: '',
      video_url: '',
      editable_link: ''
    }
  });

  const [previewPdf, setPreviewPdf] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const searchInputRef = useRef(null);
  
  // Fetch all tags on initial load and when search query changes
  const { data: allTags = [], isFetching: isLoadingAllTags } = useGetResourceTagsQuery();
  
  // Fetch tags based on search query (for when user starts typing)
  const { data: searchedTags = [], isFetching: isSearchingTags } = useSearchResourceTagsQuery(
    searchQuery.trim(),
    { skip: searchQuery.trim().length === 0 } // Always fetch, even with empty query
  );
  
  // Determine which tags to show based on search state
  const tagSuggestions = searchQuery.trim() === '' ? allTags : searchedTags;
  
  // Filter out already selected tags from suggestions
  const filteredTagSuggestions = Array.isArray(tagSuggestions) 
    ? tagSuggestions.filter(
        (tag) => tag && tag.id && !selectedTags.some((selected) => selected.id === tag.id)
      )
    : [];

  // Create tag mutation with error handling
  const [createTag, { isLoading: isCreatingTag }] = useCreateResourceTagMutation();

  // Reset form and tags when modal is closed/opened
  useEffect(() => {
    // Clear everything when modal is closed
    if (!show) {
      setSelectedTags([]);
      setSearchQuery('');
      setShowTagSuggestions(false);
      setPreviewPdf(null);
      setError('');
      return;
    }

    // Initialize form with resource data when modal is opened with a resource
    if (resource) {
      reset({
        name: resource.name || '',
        tooltip: resource.tooltip || '',
        video_url: resource.video_url || '',
        editable_link: resource.editable_link || ''
      });
      
      // Set selected tags if editing - ensure we have a fresh copy of the tags
      if (Array.isArray(resource.tags) && resource.tags.length > 0) {
        setSelectedTags([...resource.tags]);
      } else {
        setSelectedTags([]);
      }
      
      if (resource.pdf_url) {
        setPreviewPdf(resource.pdf_url);
      }
    } else {
      // Reset to defaults for new resource
      reset({
        name: '',
        tooltip: '',
        video_url: '',
        editable_link: ''
      });
      setSelectedTags([]);
      setPreviewPdf(null);
    }
  }, [show, resource, reset]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset previous errors
    setError('');
    
    // Check file type
    if (file.type !== 'application/pdf') {
      setError('Only PDF files are allowed');
      e.target.value = ''; // Clear the file input
      setPreviewPdf(null);
      return;
    }

    // Check file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setError('File size is too large (max 10MB)');
      e.target.value = ''; // Clear the file input
      setPreviewPdf(null);
      return;
    }

    // If all validations pass
    setValue('pdf_file', file);
    trigger('pdf_file');
    setPreviewPdf(URL.createObjectURL(file));
  };

  // Handle tag selection
  const handleTagSelect = (tag) => {
    if (tag && tag.id && !selectedTags.some((t) => t.id === tag.id)) {
      setSelectedTags([...selectedTags, tag]);
    }
    setSearchQuery('');
    setShowTagSuggestions(false);
    searchInputRef.current?.focus();
  };

  // Handle tag removal
  const handleTagRemove = (tagId) => {
    setSelectedTags(selectedTags.filter(tag => tag.id !== tagId));
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    setShowTagSuggestions(true);
    // Clear search query when focusing to show all tags
    setSearchQuery('');
  };

  // Handle search input blur with a small delay to allow for tag selection
  const handleSearchBlur = () => {
    // Don't hide suggestions if the blur was caused by clicking a suggestion
    if (document.activeElement === searchInputRef.current) return;
    
    setTimeout(() => {
      setShowTagSuggestions(false);
    }, 200);
  };

  // Handle create new tag
  const handleCreateTag = async () => {
    const tagName = searchQuery.trim();
    if (!tagName || isCreatingTag) return;

    try {
      // Create the new tag via API
      const result = await createTag({ 
        name: tagName,
        slug: tagName.toLowerCase().replace(/\s+/g, '-') // Generate slug from name
      }).unwrap();
      
      if (result?.data?.id) {
        // Add the new tag to the selected tags
        const newTag = { 
          id: result.data.id, 
          name: tagName,
          slug: result.data.slug || tagName.toLowerCase().replace(/\s+/g, '-')
        };
        setSelectedTags(prev => [...prev, newTag]);
        setSearchQuery('');
        setShowTagSuggestions(false);
        
        // Show success message
        Swal.fire({
          title: 'Success',
          text: `Tag "${tagName}" created successfully`,
          icon: 'success',
          confirmButtonColor: '#FD9C00',
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error creating tag:', error);
      Swal.fire({
        title: 'Error',
        text: error?.data?.message || 'Failed to create tag. Please try again.',
        icon: 'error',
        confirmButtonColor: '#FD9C00'
      });
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setError('');
    
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('tooltip', data.tooltip);
      formData.append('video_url', data.video_url || '');
      formData.append('editable_link', data.editable_link || '');
      
      // Add selected tags
      if (selectedTags.length > 0) {
        selectedTags.forEach(tag => {
          formData.append('tags[]', tag.id);
        });
      }
      
      // Only append file if it's a new file or if we're updating and a new file is selected
      if (data.pdf_file) {
        if (data.pdf_file instanceof File) {
          formData.append('pdf_file', data.pdf_file);
        } else if (resource?.presigned_pdf_url) {
          // If we're updating and no new file is selected, keep the existing file
          formData.append('pdf_url', resource.presigned_pdf_url);
        }
      }
      
      // If we're updating, include the _method parameter for Laravel to handle PUT/PATCH
      if (resource?.id) {
        formData.append('_method', 'PUT');
      }
      
      await onSaveResource(formData);
      onHide();
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.data?.message || 'An error occurred while saving the resource');
    } finally {
      setIsSubmitting(false);
    }
  };

  const removePdf = () => {
    setValue('pdf_file', null);
    trigger('pdf_file');
    setPreviewPdf(null);
  };

  return (
    <Modal show={show} onHide={onHide} centered dialogClassName="">
      <Modal.Body>
        <h4>{resource ? 'Edit Resource' : 'Add New Resource'}</h4>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>Resource Name <span className="red-star">*</span></label>
                <br />
                <input
                  type="text"
                  name="name"
                  placeholder="Enter resource name"
                  className={errors.name ? 'is-invalid' : ''}
                  {...register('name')}
                />
                {errors.name && <p className="error-text">{errors.name.message}</p>}
              </div>
            </Col>

            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <div className="d-flex align-items-center mb-2">
                  <label className="me-2">Tooltip</label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-tooltip">
                        {watch('tooltip') || 'Add a tooltip that will be shown on hover'}
                      </Tooltip>
                    }
                  >
                    <span className="info-icon">
                      <i className="bi bi-info-circle"></i>
                    </span>
                  </OverlayTrigger>
                </div>
                <textarea
                  name="tooltip"
                  placeholder="Enter tooltip text (optional)"
                  rows="3"
                  className={errors.tooltip ? 'is-invalid' : ''}
                  {...register('tooltip')}
                ></textarea>
                {errors.tooltip && <p className="error-text">{errors.tooltip.message}</p>}
              </div>
            </Col>

            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>Tags</label>
                <div className="tag-input-container">
                  {/* Selected tags */}
                  <div className="selected-tags mb-2">
                    {selectedTags.map(tag => (
                      <span key={tag.id} className="tag-badge">
                        {tag.name}
                        <button 
                          type="button" 
                          className="tag-remove"
                          onClick={(e) => {
                            e.preventDefault();
                            handleTagRemove(tag.id);
                          }}
                        >
                          <FaTimesCircle />
                        </button>
                      </span>
                    ))}
                  </div>
                  
                  {/* Tag search input */}
                  <div className="position-relative">
                    <div className="search-input-container">
                      <FaSearch className="search-icon" />
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search and add tags..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={handleSearchFocus}
                        onBlur={handleSearchBlur}
                        ref={searchInputRef}
                      />
                    </div>
                    
                    {/* Tag suggestions dropdown */}
                    {showTagSuggestions && (searchQuery.trim().length >= 2 || filteredTagSuggestions.length > 0) && (
                      <div className="tag-suggestions">
                        {(isSearchingTags || isLoadingAllTags) ? (
                          <div className="p-2 text-muted">Searching...</div>
                        ) : filteredTagSuggestions.length > 0 ? (
                          <>
                            {filteredTagSuggestions.map(tag => (
                              <div 
                                key={tag.id} 
                                className="suggestion-item"
                                onClick={() => handleTagSelect(tag)}
                              >
                                {tag.name}
                              </div>
                            ))}
                            {searchQuery.trim().length >= 2 && !filteredTagSuggestions.some(t => t.name.toLowerCase() === searchQuery.trim().toLowerCase()) && (
                              <div 
                                className={`suggestion-item create-tag ${isCreatingTag ? 'text-muted' : ''}`} 
                                onClick={!isCreatingTag ? handleCreateTag : undefined}
                              >
                                {isCreatingTag ? (
                                  <span>Creating tag "{searchQuery.trim()}"...</span>
                                ) : (
                                  <>
                                    <FaPlus className="me-2" />
                                    Create "{searchQuery.trim()}"
                                  </>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          <div 
                            className={`suggestion-item create-tag ${isCreatingTag ? 'text-muted' : ''}`} 
                            onClick={!isCreatingTag ? handleCreateTag : undefined}
                          >
                            {isCreatingTag ? (
                              <span>Creating tag "{searchQuery.trim()}"...</span>
                            ) : (
                              <>
                                <FaPlus className="me-2" />
                                Create "{searchQuery.trim()}"
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Col>

            <Col xs={12} sm={12} lg={12} md={12}>
              <label>Upload PDF</label>
              <div className="file-upload-container d-flex">
                <label className="file-upload-label">
                  <input
                    type="file"
                    id="pdf-upload"
                    accept=".pdf,application/pdf"
                    onChange={handleFileChange}
                    className={`file-upload-input ${errors.pdf_file || error ? 'is-invalid' : ''}`}
                    style={{ display: 'none' }}
                  />
                  <div>
                    <FaUpload className="file-upload-icon" />
                  </div>
                  <div>
                    <span className="file-upload-text">
                      {previewPdf ? 'PDF File Selected' : 'Please select a file'}
                    </span>
                    <span className="format-text">(Only supports .pdf files, max 10MB)</span>
                    {(errors.pdf_file || error) && (
                      <div className="error-text mt-1">
                        {errors.pdf_file?.message || error}
                      </div>
                    )}
                  </div>
                </label>
              </div>
              {previewPdf && (
                <div className="file-preview">
                  <FaFilePdf className="file-icon" />
                  <span>{previewPdf.name || 'PDF File Selected'}</span>
                  <button type="button" className="remove-file" onClick={removePdf}>
                    <FaTimes />
                  </button>
                </div>
              )}
            </Col>

            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>Video URL</label>
                <br />
                <input
                  type="text"
                  name="video_url"
                  placeholder="https://www.youtube.com/watch?v=..."
                  className={errors.video_url ? 'is-invalid' : ''}
                  {...register('video_url')}
                />
                {errors.video_url && <p className="error-text">{errors.video_url.message}</p>}
              </div>
            </Col>

            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>Editable Link</label>
                <br />
                <input
                  type="text"
                  name="editable_link"
                  placeholder="https://example.com"
                  className={errors.editable_link ? 'is-invalid' : ''}
                  {...register('editable_link')}
                />
                {errors.editable_link && <p className="error-text">{errors.editable_link.message}</p>}
              </div>
            </Col>

            {error && (
              <Col xs={12}>
                <div className="alert alert-danger">{error}</div>
              </Col>
            )}
          </Row>
          <Row>
            <div className="btn-action">
              <button
                type="button"
                className="btn-outline-black"
                onClick={onHide}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </Row>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AddAtozResourceModal;
